import React, { useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, TrendingUp } from 'lucide-react';

const font = "'Inria Serif', serif";

// Day-of-week labels, padded to 3 chars so columns align nicely.
const DOW_LABELS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

function formatEuro(amount) {
  return `${(amount || 0).toFixed(2).replace(/\.00$/, '')}€`;
}

/**
 * Slide-up bottom sheet showing the most recent 7 days of activity for both
 * Total Profit and Transactions. Reads `daily_data` from the analytics
 * response — no extra fetch required.
 *
 * Props:
 *  - open        : boolean
 *  - onClose     : () => void
 *  - data        : analytics API response (must include daily_data, year, month)
 *  - metric      : 'profit' | 'transactions' — which one the user tapped
 */
export default function VenueAnalyticsWeeklySheet({ open, onClose, data, metric = 'profit' }) {
  const isProfit = metric === 'profit';
  const accentColor = isProfit ? 'var(--status-success-text)' : 'var(--accent-purple)';
  const accentSurface = isProfit ? 'var(--status-success-surface)' : 'var(--accent-surface-soft)';
  const accentBorder = isProfit ? 'var(--status-success-text)' : 'var(--card-border)';
  const accent = accentColor;

  const rows = useMemo(() => {
    if (!data?.daily_data?.length) return [];
    const { year, month } = data;
    // Take the latest 7 entries from the period so the sheet always shows the
    // most recent activity even if the month/range is incomplete.
    const last7 = data.daily_data.slice(-7);
    return last7.map((d) => {
      // Prefer the precise month index emitted by the backend so the day-of-week
      // is correct even when the rolling window straddles month boundaries.
      const monthIdx = typeof d.month_idx === 'number' ? d.month_idx : ((month || 1) - 1);
      const yearForRow = year || new Date().getFullYear();
      const dt = new Date(yearForRow, monthIdx, d.day);
      return {
        day: d.day,
        label: d.label,
        dow: DOW_LABELS[(dt.getDay() + 6) % 7],
        value: isProfit ? Number(d.profit || 0) : Number(d.qr_count || 0),
      };
    });
  }, [data, isProfit]);

  const maxValue = useMemo(() => rows.reduce((a, r) => Math.max(a, r.value), 0) || 1, [rows]);
  const totalValue = rows.reduce((a, r) => a + r.value, 0);
  const avgValue = rows.length ? totalValue / rows.length : 0;

  // Compute a friendly "vs. last 7d before that" delta — same pattern as MoM
  // but rolling. We only show it when positive (per product spec: only
  // celebrate growth, never surface a negative number).
  const priorRows = useMemo(() => {
    if (!data?.daily_data?.length || data.daily_data.length < 14) return [];
    return data.daily_data.slice(-14, -7);
  }, [data]);
  const priorTotal = priorRows.reduce((a, r) => a + (isProfit ? Number(r.profit || 0) : Number(r.qr_count || 0)), 0);
  const wowPct = priorTotal > 0 ? Math.round(((totalValue - priorTotal) / priorTotal) * 100) : 0;
  const showWow = priorTotal > 0 && wowPct > 0;

  return (
    <AnimatePresence>
      {open ? (
        <>
          {/* Scrim */}
          <motion.div
            data-testid="venue-analytics-weekly-sheet-scrim"
            onClick={onClose}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.18 }}
            style={{
              position: 'fixed',
              inset: 0,
              background: 'rgba(0, 0, 0, 0.32)',
              zIndex: 80,
            }}
          />
          {/* Sheet */}
          <motion.div
            data-testid="venue-analytics-weekly-sheet"
            initial={{ y: '100%' }}
            animate={{ y: 0 }}
            exit={{ y: '100%' }}
            transition={{ type: 'spring', damping: 30, stiffness: 280 }}
            style={{
              position: 'fixed',
              left: 0,
              right: 0,
              bottom: 0,
              zIndex: 81,
              background: 'var(--surface)',
              borderTopLeftRadius: 28,
              borderTopRightRadius: 28,
              padding: '14px 18px 28px',
              boxShadow: '0 -12px 30px rgba(0, 0, 0, 0.18)',
              maxHeight: '78vh',
              overflowY: 'auto',
            }}
          >
            {/* Drag handle */}
            <div aria-hidden="true" style={{ width: 44, height: 4, borderRadius: 2, background: 'var(--hairline)', margin: '4px auto 14px' }} />

            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14 }}>
              <div>
                <p style={{ margin: 0, fontFamily: font, fontSize: 11, letterSpacing: 1.1, textTransform: 'uppercase', color: 'var(--text-3)', fontWeight: 700 }}>
                  Last 7 days
                </p>
                <p style={{ margin: '2px 0 0', fontFamily: font, fontSize: 22, fontWeight: 700, color: 'var(--text)', lineHeight: 1.1 }}>
                  {isProfit ? 'Profit breakdown' : 'Transactions breakdown'}
                </p>
              </div>
              <button
                type="button"
                onClick={onClose}
                data-testid="venue-analytics-weekly-sheet-close"
                aria-label="Close"
                style={{
                  width: 36,
                  height: 36,
                  borderRadius: 999,
                  border: '1px solid var(--card-border)',
                  background: 'var(--surface)',
                  cursor: 'pointer',
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: 'var(--text)',
                }}
              >
                <X size={18} />
              </button>
            </div>

            {/* Header stats */}
            <div style={{ display: 'flex', gap: 10, marginBottom: 16 }}>
              <div style={{ flex: 1, padding: '10px 12px', borderRadius: 14, background: accentSurface, border: `1px solid ${accentBorder}` }}>
                <p style={{ margin: 0, fontFamily: font, fontSize: 11, color: 'var(--text-2)', letterSpacing: 0.6 }}>Total</p>
                <p style={{ margin: '2px 0 0', fontFamily: font, fontSize: 18, fontWeight: 700, color: accent }}>
                  {isProfit ? formatEuro(totalValue) : totalValue.toLocaleString()}
                </p>
              </div>
              <div style={{ flex: 1, padding: '10px 12px', borderRadius: 14, background: 'var(--surface-2)', border: '1px solid var(--hairline)' }}>
                <p style={{ margin: 0, fontFamily: font, fontSize: 11, color: 'var(--text-2)', letterSpacing: 0.6 }}>Daily avg</p>
                <p style={{ margin: '2px 0 0', fontFamily: font, fontSize: 18, fontWeight: 700, color: 'var(--text)' }}>
                  {isProfit ? formatEuro(avgValue) : Math.round(avgValue).toLocaleString()}
                </p>
              </div>
            </div>

            {showWow ? (
              <div style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '4px 10px', borderRadius: 9999, background: 'var(--status-success-surface)', color: 'var(--status-success-text)', fontFamily: font, fontSize: 12, fontWeight: 700, marginBottom: 14 }}>
                <TrendingUp size={12} strokeWidth={2.5} />
                +{wowPct}% vs the 7 days before
              </div>
            ) : null}

            {/* Day rows with horizontal bars */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {rows.map((r) => {
                const ratio = Math.max(0.06, r.value / maxValue);
                return (
                  <div key={`row-${r.day}`} data-testid={`venue-analytics-weekly-row-${r.day}`} style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    <span style={{ width: 36, fontFamily: font, fontSize: 13, color: 'var(--text-3)' }}>{r.dow}</span>
                    <span style={{ width: 22, fontFamily: font, fontSize: 13, fontWeight: 700, color: 'var(--text)' }}>{r.day}</span>
                    <div style={{ flex: 1, height: 10, background: 'var(--surface-2)', borderRadius: 9999, overflow: 'hidden' }}>
                      <div
                        style={{
                          width: `${ratio * 100}%`,
                          height: '100%',
                          borderRadius: 9999,
                          background: accent,
                          transition: 'width 220ms ease',
                        }}
                      />
                    </div>
                    <span style={{ width: 64, textAlign: 'right', fontFamily: font, fontSize: 13, fontWeight: 700, color: 'var(--text)' }}>
                      {isProfit ? formatEuro(r.value) : r.value.toLocaleString()}
                    </span>
                  </div>
                );
              })}
              {rows.length === 0 ? (
                <p style={{ margin: '10px 0', textAlign: 'center', fontFamily: font, fontSize: 13, color: 'var(--text-3)' }}>
                  Not enough activity yet for this period.
                </p>
              ) : null}
            </div>
          </motion.div>
        </>
      ) : null}
    </AnimatePresence>
  );
}
